import React from "react";
import i18next from "src/i18n/config";
import { RootState, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { filesize } from "src/ui/utils/filesize";

import { selectDevicesByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { Device, GroupMember } from "@dashboard/devices/types";
import { FileInfo, FileStatus, FileTableItem, SourceType } from "@dashboard/files/types";
import { getFileSourceKey, sourceConfig } from "@dashboard/files/utils/fileSource";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { createRolloutTag } from "@dashboard/shared/tags";
import { useCurrentSubscription, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import { Box } from "@mui/material";
import UserSettingsService from "@services/userSettingsService";
import { IndicatorIcon } from "@shared/IndicatorIcon";
import { DateCell } from "@shared/table/DateCell";
import { MinimalTableCell, MinimalTableCellWithTooltip, TruncatedText } from "@shared/table/MinimalTableComponents";
import { createColumnHelper } from "@tanstack/react-table";

import FileIcon from "../FileIcon";
import { ActionCell, FileTags, VisibilityChangeCell } from "../TableItem/TableItem";

import { FilesTableParams } from "./UploadTable";

export type FileExtendedTableItem = FileTableItem & { warnings: string[] };

const columnHelper = createColumnHelper<FileExtendedTableItem>();

export const tableColumns = [
    columnHelper.accessor("contentType", {
        header: i18next.t("common:label.type"),
        cell: (info) => <FileIcon row={info.row} />,
        enableSorting: false,
        meta: { align: "center" },
    }),
    columnHelper.accessor("filename", {
        header: i18next.t("common:label.name"),
        cell: (info) => (
            <MinimalTableCell fluid fluidMinWidth="20ch">
                <TruncatedText title={info.getValue()} highlight={info.table.options.meta?.searchQuery} testid="file-name-cell" />
                <FileTags row={info.row} />
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("version", {
        id: "version",
        header: i18next.t("common:label.version"),
        enableSorting: false,
        cell: (info) => <MinimalTableCell>{info.getValue() ?? "-"}</MinimalTableCell>,
    }),
    columnHelper.accessor((row) => filesize(row.size), {
        id: "fileSize",
        header: i18next.t("common:label.size"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue()} testid="file-size-cell" style={{ minWidth: 100 }} />,
        enableSorting: true,
    }),
    columnHelper.accessor((row) => row.modifiedDate ?? row.createdDate, {
        id: "modifiedDate",
        header: i18next.t("common:label.updated"),
        enableSorting: true,
        cell: (info) => <DateCell value={info.getValue()} />,
    }),
    columnHelper.accessor("visibility", {
        header: i18next.t("common:label.source"),
        meta: { headerTooltip: i18next.t("common:tooltip.sourceHeader") },
        enableSorting: false,
        cell: (info) => {
            const selectedGroupId = info.table.options.meta?.selectedGroupId;
            const source = getFileSourceKey(info.row.original.groupId, selectedGroupId);
            const { tooltip, icon } = sourceConfig[source];
            return (
                <MinimalTableCell>
                    <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <IndicatorIcon icon={`fa-solid ${icon}`} tooltip={tooltip} />
                        <VisibilityChangeCell {...info} />
                    </Box>
                </MinimalTableCell>
            );
        },
    }),
    columnHelper.display({
        id: "action",
        header: "",
        cell: (info) => <ActionCell row={info.row} />,
    }),
];

const getUploader = (sourceType: SourceType, member: WorkspaceMember | GroupMember | undefined, device: Device | undefined) => {
    if (sourceType === SourceType.User && member?.email) {
        return member.email;
    } else if (sourceType === SourceType.Device && device?.name) {
        return device.name;
    } else if (sourceType === SourceType.Microservice) {
        return "OS CI";
    } else {
        return "-";
    }
};

export const useTransformFilesToTableData = (files: FileInfo[]): FileTableItem[] => {
    const groups = useSelector(selectGroups);
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds || [];
    const devices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds));
    const { data: currentWorkspace } = useCurrentWorkspace();

    return React.useMemo(() => {
        return files.map((file) => {
            const device = devices.find((item) => String(item.id) === file.ownerId);
            const group = groups.find((item) => item.id === file.groupId);
            const members = file.workspaceId ? currentWorkspace?.members : group?.members;
            const member = members?.find((item) => item.userId === file.ownerId);
            const workspaceName = currentWorkspace?.name;

            const removableTags = file.metadata?.user?.tags;
            const rolloutTag = createRolloutTag(file.rolloutPercentage);

            return {
                ...file,
                filename: file.filename,
                status: file.status,
                version: file.metadata?.version ?? file.metadata?.file?.apk?.manifest?.versioncode,
                groupName: group?.name || "-",
                groupId: group?.id ?? file.groupId ?? null,
                uploader: getUploader(file.source, member, device),
                visibility: group?.name ?? workspaceName,
                removableTags: removableTags,
                rolloutTag: rolloutTag,
                hasTags: removableTags?.length || rolloutTag,
                isUploading: file.status === FileStatus.Pending,
            };
        });
    }, [groups, devices, files, currentWorkspace]);
};

export function useParams(): [FilesTableParams, React.Dispatch<React.SetStateAction<FilesTableParams>>] {
    const initialParams = UserSettingsService.getFilePageParams();

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    if (workspaceId !== null) {
        initialParams.workspaceId = workspaceId;
    }

    // The table has no uploader column any more, so an uploader filter or sort saved by an older
    // version would keep narrowing or ordering the list with no control left to clear it.
    initialParams.sourceTypes = [];
    if (initialParams.sortCriteria === "ownerId") {
        initialParams.sortCriteria = "modifiedDate";
        initialParams.sortDirection = "desc";
    }

    const [params, setParams] = React.useState<FilesTableParams>(initialParams);

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("filePage.params", params);
    }, [params, workspaceId]);

    return [params, setParams];
}
